/**
 * Challenge difficulty bands.
 *
 * A band is derived from `expectedScore(myRating, theirRating)` — the same
 * function that rates the match afterwards — so "evenly matched" on the
 * challenge page means exactly what it will mean when the result is applied.
 * There is no second opinion about who is a fair fight.
 */
import { PROVISIONAL_GAMES } from './constants.js';
import { expectedScore } from './elo.js';
export const CHALLENGE_BANDS = ['easy', 'favourite', 'even', 'underdog', 'nochance'];
export const CHALLENGE_STATUSES = [
    'open',
    'accepted',
    'declined',
    'cancelled',
    'expired',
    'played',
];
/**
 * Lower bound on *my* win probability for each band, checked highest first.
 *
 * For reference, at the default K these correspond to roughly:
 *   0.75 → ~190 rating points ahead
 *   0.60 → ~70 points ahead
 *   0.40 → ~70 points behind
 *   0.25 → ~190 points behind
 */
const BAND_FLOORS = [
    ['easy', 0.75],
    ['favourite', 0.6],
    ['even', 0.4],
    ['underdog', 0.25],
    ['nochance', 0],
];
export const CHALLENGE_BAND_INFO = {
    easy: { band: 'easy', label: 'Easy game', blurb: 'You should win comfortably' },
    favourite: { band: 'favourite', label: "You're favourite", blurb: 'Winnable, but not a given' },
    even: { band: 'even', label: 'Evenly matched', blurb: 'Close to a coin flip' },
    underdog: { band: 'underdog', label: "You're the underdog", blurb: 'You will have to work' },
    nochance: { band: 'nochance', label: "I've got no chance", blurb: 'Bravery, or a lesson' },
};
/** Bands ordered easiest → hardest, for rendering the picker. */
export const CHALLENGE_BANDS_ORDERED = CHALLENGE_BANDS;
/** Which band does this win probability fall into? */
export function bandForExpectedScore(expected) {
    for (const [band, floor] of BAND_FLOORS) {
        if (expected >= floor)
            return band;
    }
    return 'nochance';
}
/** Convenience: band between two ratings, from the first player's point of view. */
export function bandForRatings(myRating, theirRating) {
    return bandForExpectedScore(expectedScore(myRating, theirRating));
}
/**
 * A band is only meaningful if both ratings mean something. Below the
 * provisional threshold a rating is still converging, so the band is a guess
 * and the UI says so rather than quietly overstating its confidence.
 */
export function isBandReliable(myGames, theirGames) {
    return myGames >= PROVISIONAL_GAMES && theirGames >= PROVISIONAL_GAMES;
}
/** How long an unanswered challenge stays open. */
export const CHALLENGE_TTL_DAYS = 14;
export const MAX_CHALLENGE_MESSAGE_LENGTH = 200;
//# sourceMappingURL=challenges.js.map